export const COMPANY_ITEM_MASTER_DEFAULT_PAGE_SIZE = 10;
export const COMPANY_ITEM_MASTER_PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"];
export const COMPANY_ITEM_MASTER_TABLE_EMPTY_VALUE = "-";
export const COMPANY_ITEM_MASTER_MAX_TEXT_LENGTH = 255;
export const COMPANY_ITEM_MASTER_JAN_CODE_MAX_LENGTH = 13;
export const COMPANY_ITEM_MASTER_JAN_CODE_PATTERN = /^(?:\d{8}|\d{13})$/;
export const COMPANY_ITEM_MASTER_JAN_CODE_MATCH_TYPE_PREFIX = "prefix";
export const COMPANY_ITEM_MASTER_JAN_CODE_MATCH_TYPE_EXACT = "exact";
export const COMPANY_ITEM_MASTER_JAN_CODE_MATCH_TYPE_OPTIONS = [
  { value: COMPANY_ITEM_MASTER_JAN_CODE_MATCH_TYPE_EXACT, labelKey: "itemMaster.janCodeMatchExact" },
  { value: COMPANY_ITEM_MASTER_JAN_CODE_MATCH_TYPE_PREFIX, labelKey: "itemMaster.janCodeMatchPrefix" },
] as const;

export type CompanyItemMasterJanCodeMatchType = (typeof COMPANY_ITEM_MASTER_JAN_CODE_MATCH_TYPE_OPTIONS)[number]["value"];

export type CompanyItemMasterFormValue = {
  jan_code: string;
  repeat: number | null;
  part_number: string;
  sample_number: string;
  item_name: string;
  brand_name: string;
  size: string;
  color_no: string;
};

export type CompanyItemMasterSearchFormValue = {
  jan_code: string;
  jan_code_match_type: CompanyItemMasterJanCodeMatchType;
  part_number: string;
  item_name: string;
  brand_name: string;
};

export type CompanyItemMasterRequestBody = {
  jan_code: string;
  repeat: number | null;
  part_number: string;
  sample_number: string | null;
  item_name: string;
  brand_name: string;
  size: string | null;
  color_no: string;
};

export type CompanyItemMasterResponseType = {
  id: number;
  jan_code: string | null;
  repeat: number | null;
  part_number: string | null;
  sample_number: string | null;
  item_name: string | null;
  brand_name: string | null;
  size: string | null;
  color_no: string | null;
  created_at: string | null;
  updated_at: string | null;
  company?: {
    id: number;
    company_name?: string | null;
  };
};

export type CompanyItemMasterListResponse = {
  data?: CompanyItemMasterResponseType[];
  meta?: {
    total?: number;
    page?: number;
    limit?: number;
  };
};

export type CompanyItemMasterFieldError = Partial<Record<keyof CompanyItemMasterFormValue, boolean>>;

type CompanyItemMasterValidationResult = {
  isValid: boolean;
  errors: CompanyItemMasterFieldError;
  messageKey?: string;
  messageValues?: Record<string, number>;
};

const toTrimmedString = (value: string) => value.trim();
const toJanCodeMatchTypeSearchValue = (value: string): CompanyItemMasterJanCodeMatchType => (
  value === COMPANY_ITEM_MASTER_JAN_CODE_MATCH_TYPE_PREFIX
    ? COMPANY_ITEM_MASTER_JAN_CODE_MATCH_TYPE_PREFIX
    : COMPANY_ITEM_MASTER_JAN_CODE_MATCH_TYPE_EXACT
);
const COMPANY_ITEM_MASTER_JAN_CODE_SEARCH_PATTERN = /^\d+$/;

export type CompanyItemMasterSearchValidationResult = {
  isValid: boolean;
  message?: string;
  messageKey?: string;
  messageValues?: Record<string, number>;
};

export const validateCompanyItemMasterJanCodeSearch = (
  value: Pick<CompanyItemMasterSearchFormValue, "jan_code">,
): CompanyItemMasterSearchValidationResult => {
  const janCode = toTrimmedString(value.jan_code);

  if (!janCode) {
    return { isValid: true };
  }

  if (!COMPANY_ITEM_MASTER_JAN_CODE_SEARCH_PATTERN.test(janCode)) {
    return { isValid: false, messageKey: "itemMaster.validation.janCodeDigitsOnly" };
  }

  if (janCode.length > COMPANY_ITEM_MASTER_JAN_CODE_MAX_LENGTH) {
    return {
      isValid: false,
      messageKey: "itemMaster.validation.janCodeMaxLength",
      messageValues: { max: COMPANY_ITEM_MASTER_JAN_CODE_MAX_LENGTH },
    };
  }

  return { isValid: true };
};

export const createDefaultCompanyItemMasterForm = (): CompanyItemMasterFormValue => ({
  jan_code: "",
  repeat: null,
  part_number: "",
  sample_number: "",
  item_name: "",
  brand_name: "",
  size: "",
  color_no: "",
});

export const createDefaultCompanyItemMasterSearchForm = (): CompanyItemMasterSearchFormValue => ({
  jan_code: "",
  jan_code_match_type: COMPANY_ITEM_MASTER_JAN_CODE_MATCH_TYPE_EXACT,
  part_number: "",
  item_name: "",
  brand_name: "",
});

export const normalizeCompanyItemMasterSearchForm = (
  value: CompanyItemMasterSearchFormValue,
): CompanyItemMasterSearchFormValue => ({
  jan_code: toTrimmedString(value.jan_code),
  jan_code_match_type: toJanCodeMatchTypeSearchValue(value.jan_code_match_type),
  part_number: toTrimmedString(value.part_number).slice(0, COMPANY_ITEM_MASTER_MAX_TEXT_LENGTH),
  item_name: toTrimmedString(value.item_name).slice(0, COMPANY_ITEM_MASTER_MAX_TEXT_LENGTH),
  brand_name: toTrimmedString(value.brand_name).slice(0, COMPANY_ITEM_MASTER_MAX_TEXT_LENGTH),
});

export const mapCompanyItemMasterResponseToForm = (
  value: CompanyItemMasterResponseType,
): CompanyItemMasterFormValue => ({
  jan_code: value.jan_code ?? "",
  repeat: value.repeat ?? null,
  part_number: value.part_number ?? "",
  sample_number: value.sample_number ?? "",
  item_name: value.item_name ?? "",
  brand_name: value.brand_name ?? "",
  size: value.size ?? "",
  color_no: value.color_no ?? "",
});

export const toCompanyItemMasterRequestBody = (
  value: CompanyItemMasterFormValue,
): CompanyItemMasterRequestBody => ({
  jan_code: toTrimmedString(value.jan_code),
  repeat: value.repeat,
  part_number: toTrimmedString(value.part_number),
  sample_number: toTrimmedString(value.sample_number) || null,
  item_name: toTrimmedString(value.item_name),
  brand_name: toTrimmedString(value.brand_name),
  size: toTrimmedString(value.size) || null,
  color_no: toTrimmedString(value.color_no),
});

export const validateCompanyItemMasterForm = (
  value: CompanyItemMasterFormValue,
): CompanyItemMasterValidationResult => {
  const errors: CompanyItemMasterFieldError = {};

  const janCode = toTrimmedString(value.jan_code);
  if (!janCode) {
    errors.jan_code = true;
    return { isValid: false, errors, messageKey: "itemMaster.validation.janCodeRequired" };
  }
  if (!COMPANY_ITEM_MASTER_JAN_CODE_PATTERN.test(janCode)) {
    errors.jan_code = true;
    return { isValid: false, errors, messageKey: "itemMaster.validation.janCodeInvalidLength" };
  }

  if (value.repeat !== null && (!Number.isInteger(value.repeat) || Number(value.repeat) < 0)) {
    errors.repeat = true;
    return { isValid: false, errors, messageKey: "itemMaster.validation.repeatNonNegativeInteger" };
  }

  const partNumber = toTrimmedString(value.part_number);
  if (!partNumber) {
    errors.part_number = true;
    return { isValid: false, errors, messageKey: "itemMaster.validation.partNumberRequired" };
  }
  if (partNumber.length > COMPANY_ITEM_MASTER_MAX_TEXT_LENGTH) {
    errors.part_number = true;
    return {
      isValid: false,
      errors,
      messageKey: "itemMaster.validation.partNumberMaxLength",
      messageValues: { max: COMPANY_ITEM_MASTER_MAX_TEXT_LENGTH },
    };
  }

  const sampleNumber = toTrimmedString(value.sample_number);
  if (sampleNumber.length > COMPANY_ITEM_MASTER_MAX_TEXT_LENGTH) {
    errors.sample_number = true;
    return {
      isValid: false,
      errors,
      messageKey: "itemMaster.validation.sampleNumberMaxLength",
      messageValues: { max: COMPANY_ITEM_MASTER_MAX_TEXT_LENGTH },
    };
  }

  const itemName = toTrimmedString(value.item_name);
  if (!itemName) {
    errors.item_name = true;
    return { isValid: false, errors, messageKey: "itemMaster.validation.itemNameRequired" };
  }
  if (itemName.length > COMPANY_ITEM_MASTER_MAX_TEXT_LENGTH) {
    errors.item_name = true;
    return {
      isValid: false,
      errors,
      messageKey: "itemMaster.validation.itemNameMaxLength",
      messageValues: { max: COMPANY_ITEM_MASTER_MAX_TEXT_LENGTH },
    };
  }

  const brandName = toTrimmedString(value.brand_name);
  if (!brandName) {
    errors.brand_name = true;
    return { isValid: false, errors, messageKey: "itemMaster.validation.brandNameRequired" };
  }
  if (brandName.length > COMPANY_ITEM_MASTER_MAX_TEXT_LENGTH) {
    errors.brand_name = true;
    return {
      isValid: false,
      errors,
      messageKey: "itemMaster.validation.brandNameMaxLength",
      messageValues: { max: COMPANY_ITEM_MASTER_MAX_TEXT_LENGTH },
    };
  }

  const size = toTrimmedString(value.size);
  if (size.length > COMPANY_ITEM_MASTER_MAX_TEXT_LENGTH) {
    errors.size = true;
    return {
      isValid: false,
      errors,
      messageKey: "itemMaster.validation.sizeMaxLength",
      messageValues: { max: COMPANY_ITEM_MASTER_MAX_TEXT_LENGTH },
    };
  }

  const colorNo = toTrimmedString(value.color_no);
  if (!colorNo) {
    errors.color_no = true;
    return { isValid: false, errors, messageKey: "itemMaster.validation.colorNoRequired" };
  }
  if (colorNo.length > COMPANY_ITEM_MASTER_MAX_TEXT_LENGTH) {
    errors.color_no = true;
    return {
      isValid: false,
      errors,
      messageKey: "itemMaster.validation.colorNoMaxLength",
      messageValues: { max: COMPANY_ITEM_MASTER_MAX_TEXT_LENGTH },
    };
  }

  return { isValid: true, errors: {} };
};

export const getCompanyItemMasterDisplayValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return COMPANY_ITEM_MASTER_TABLE_EMPTY_VALUE;
  }

  if (typeof value === "string" && value.trim() === "") {
    return COMPANY_ITEM_MASTER_TABLE_EMPTY_VALUE;
  }

  return String(value);
};
